import React, { useEffect } from 'react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import Pagination from 'rsuite/Pagination';
import Table from 'rsuite/Table';
import { RouterInput, trpc } from '../../../api/trpc';
import { usePaginationQs } from '../../../utils/usePagination';
import { ModalsService } from '../../../services/modals.service';
import { AlertService } from '../../../services/alert.service';
import { RbacObject } from '../../../export-types/cleaned-types';
import { Rbac } from '../../common/Rbac';
import { ConfirmModal } from '../../common/confirm-modal';
import { ImagesCell } from '../../common/cards/images-cell-card.component';
import { ToggleCell } from '../../common/table/toggle-cell';
import MyLoader from '../../common/loader.component';
import { AddOtcRecommendation } from './AddOtcRecommendation';
import type { Item } from './OtcRecommendationTab';
import { RecommendationActions } from './RecommendationActions';

export const PoizonRecommendationTab = () => {
  const { take, skip, page, handleSetPage } = usePaginationQs(10);
  const input = {
    provider: 'Poizon' as const,
    take,
    skip,
    orderBy: { id: 'desc' as const },
  };
  const { data, isLoading, mutate } = useSWR(
    ['otcRecommendationAdmin.list', input],
    () => trpc.otcRecommendationAdmin.list.query(input),
  );
  useEffect(() => {
    if (!data) return;
    const lastPage = Math.max(1, Math.ceil(data.total / take));
    if (page > lastPage) handleSetPage(lastPage);
  }, [data, page, take, handleSetPage]);
  const { trigger: update } = useSWRMutation(
    ['otcRecommendationAdmin.update'],
    (_key, { arg }: { arg: RouterInput['otcRecommendationAdmin']['update'] }) =>
      trpc.otcRecommendationAdmin.update.mutate(arg),
  );
  const { trigger: remove } = useSWRMutation(
    ['otcRecommendationAdmin.delete'],
    (_key, { arg }: { arg: RouterInput['otcRecommendationAdmin']['delete'] }) =>
      trpc.otcRecommendationAdmin.delete.mutate(arg),
  );

  const edit = (item: Item) => {
    ModalsService.createModal(AddOtcRecommendation, {
      item,
    }).then(() => mutate());
  };
  const deleteItem = (item: Item) => {
    ModalsService.createModal(ConfirmModal, {
      title: 'Удалить рекомендацию?',
      buttonText: 'Удалить',
    }).then(async () => {
      await remove({ id: item.id });
      await mutate();
      AlertService.success();
    });
  };
  const toggleDisabled = async (item: Item, disabled: boolean) => {
    await update({ id: item.id, data: { disabled } });
    await mutate();
    AlertService.success();
  };

  return (
    <div>
      {isLoading ? (
        <MyLoader />
      ) : (
        <Table
          data={data?.items || []}
          headerHeight={57}
          rowHeight={65}
          autoHeight
          rowKey="id"
        >
          <Table.Column fullText width={50}>
            <Table.HeaderCell>Id</Table.HeaderCell>
            <Table.Cell dataKey="id" />
          </Table.Column>
          <Table.Column fullText width={100}>
            <Table.HeaderCell>Фото</Table.HeaderCell>
            <Table.Cell>
              {({ image }: Item) =>
                image ? (
                  <ImagesCell imagesList={[image]} width={48} height={48} />
                ) : null
              }
            </Table.Cell>
          </Table.Column>
          <Table.Column fullText width={180}>
            <Table.HeaderCell>Артикул</Table.HeaderCell>
            <Table.Cell dataKey="objectId" />
          </Table.Column>
          <Table.Column fullText flexGrow={1} minWidth={150}>
            <Table.HeaderCell>Название товара</Table.HeaderCell>
            <Table.Cell dataKey="name" style={{ whiteSpace: 'normal' }} />
          </Table.Column>
          <Table.Column fullText width={100}>
            <Table.HeaderCell>Показы</Table.HeaderCell>
            <Table.Cell dataKey="views" />
          </Table.Column>
          <Table.Column fullText width={100}>
            <Table.HeaderCell>Клики</Table.HeaderCell>
            <Table.Cell dataKey="clicks" />
          </Table.Column>
          <Table.Column fullText width={100}>
            <Table.HeaderCell>CTR%</Table.HeaderCell>
            <Table.Cell>
              {({ ctr }: Item) =>
                ctr === undefined ? null : (ctr * 100).toFixed(6)
              }
            </Table.Cell>
          </Table.Column>
          <Table.Column fullText width={100}>
            <Table.HeaderCell>Включен</Table.HeaderCell>
            <Table.Cell>
              {(item: Item) => (
                <Rbac
                  object={RbacObject.OtcRecommendation}
                  action="write:admin"
                  otherwise={() => <span>{item.disabled ? 'Нет' : 'Да'}</span>}
                >
                  <ToggleCell
                    renderAsCell={false}
                    rowData={item}
                    dataKey="disabled"
                    inverted
                    onChange={toggleDisabled}
                  />
                </Rbac>
              )}
            </Table.Cell>
          </Table.Column>
          <Table.Column fullText fixed="right">
            <Table.HeaderCell> </Table.HeaderCell>
            <Table.Cell className="link-group no-padding-cell" align="right">
              {(item: Item) => (
                <Rbac
                  object={RbacObject.OtcRecommendation}
                  action="write:admin"
                >
                  <RecommendationActions
                    onEdit={() => edit(item)}
                    onDelete={() => deleteItem(item)}
                  />
                </Rbac>
              )}
            </Table.Cell>
          </Table.Column>
        </Table>
      )}
      {data && data.total > take && (
        <Pagination
          className="mt-2 pb-4"
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          layout={['pager']}
          total={data.total}
          limit={take}
          activePage={page}
          onChangePage={handleSetPage}
        />
      )}
    </div>
  );
};
